import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { Switch } from 'src/component/ui/switch'
import { EmailNotificationSvgIcon } from 'src/component/util/images'
import { messageDao } from 'src/dao/message-dao'
import { logger } from 'src/util/logger'
import { queryKeys } from 'src/util/query-keys'
import { toasterUtil } from 'src/util/toaster-util'

export const Preferences = (): React.ReactElement => {
  const queryClient = useQueryClient()
  const [localEmailNotifications, setLocalEmailNotifications] = useState<boolean>(false)

  const { data: emailNotifications = false, isLoading: isFetching } = useQuery<boolean, Error>(
    queryKeys.emailNotifications(),
    async (): Promise<boolean> => {
      return await messageDao.getMyOptIn()
    },
    {
      onSuccess: (data: boolean) => {
        setLocalEmailNotifications(data)
      },
      onError: (err: Error) => {
        logger.error('Failed to fetch email notification status:', err)
      },
    },
  )

  const { mutate: updateEmailNotifications, isLoading: isUpdating } = useMutation<void, Error, boolean>(
    async (isOptingIn: boolean): Promise<void> => {
      await messageDao.setMyOptIn({ isOptingIn })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.emailNotifications())
        toasterUtil.success('Email notification preferences updated')
      },
      onError: (err: Error) => {
        logger.error('Failed to update email notification status:', err)
        toasterUtil.error('Failed to update email notification preferences')
        setLocalEmailNotifications(emailNotifications)
      },
    },
  )

  const handleEmailToggle = useCallback(
    (checked: boolean) => {
      setLocalEmailNotifications(checked)
      updateEmailNotifications(checked)
    },
    [updateEmailNotifications],
  )

  const isLoading = isFetching || isUpdating

  return (
    <div className="p-6 md:p-10 bg-primary-gray rounded-md">
      <h3 className="font-evogria text-2xl md:text-4xl text-primary uppercase mb-10">Preferences</h3>
      <div className="flex items-center justify-between !mb-5">
        <h6 className="font-evogria text-sm md:text-base text-ivory uppercase">Notifications</h6>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EmailNotificationSvgIcon />
            <label className="block text-sm font-roboto-condensed font-bold text-primary mb-0">
              Allow marketing & promotional emails
            </label>
          </div>
          <Switch checked={localEmailNotifications} onCheckedChange={handleEmailToggle} disabled={isLoading} />
        </div>
      </div>
    </div>
  )
}
